import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Download } from "lucide-react";
import { track } from "@/lib/analytics";
import manifest from "@/data/sampleManifest.json";
import expectationText from "@/data/curriculumExpectations.json";
import ExpectationPill from "@/components/ExpectationPill";

const PREVIEW_BASE = (slug) => `/samples/previews/${slug}`;
const FILES_BASE = (slug) => `/samples/files/${slug}`;

function MetaItem({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#758d9f]">
        {label}
      </span>
      <span className="mt-0.5 text-[13px] font-bold text-[#1c2d3d]">{value}</span>
    </div>
  );
}

function ArtifactCard({ lesson, artifact, index, isPackage }) {
  const cardImg = `${PREVIEW_BASE(lesson.slug)}/${artifact.slug}-card.webp`;
  const fileHref = `${FILES_BASE(lesson.slug)}/${artifact.file}`;
  const pptxHref =
    artifact.source_format === "pptx"
      ? `${FILES_BASE(lesson.slug)}/${artifact.file.replace(/\.pdf$/, ".pptx")}`
      : null;

  // Lightweight, non-PII click tracking → PostHog (view in the dashboard by
  // filtering these event names / breaking down by `artifact_slug`).
  const fire = (event, format) =>
    track(event, {
      lesson_slug: lesson.slug,
      artifact_slug: artifact.slug,
      artifact_name: artifact.name,
      pages: artifact.pages,
      ...(format ? { format } : {}),
    });

  return (
    <article
      data-testid={`artifact-card-${artifact.slug}`}
      className={[
        "overflow-hidden rounded-2xl bg-white",
        isPackage
          ? "border-2 border-[#96ded1] shadow-[0_2px_24px_rgba(74,105,132,0.12)]"
          : "border border-[#4a6984]/25 shadow-sm",
      ].join(" ")}
    >
      {isPackage && (
        <div
          data-testid="artifact-package-ribbon"
          className="bg-[#96ded1]/20 px-6 py-2 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]"
        >
          The whole thing, in one file
        </div>
      )}

      <div className="w-full border-b border-[#4a6984]/15 bg-[#f4f5f7]">
        <img
          data-testid={`artifact-preview-${artifact.slug}`}
          src={cardImg}
          alt={`${artifact.name} — page 1`}
          loading={index === 0 ? "eager" : "lazy"}
          className="block w-full h-auto"
        />
      </div>

      <div className="px-5 py-5 sm:px-6">
        <h3
          data-testid={`artifact-name-${artifact.slug}`}
          className="text-[17px] font-extrabold leading-snug text-[#1c2d3d]"
        >
          {artifact.name}
        </h3>
        <p className="mt-2 text-[14px] leading-relaxed text-[#4a6984]">
          {artifact.purpose}
        </p>
        <p
          data-testid={`artifact-pages-${artifact.slug}`}
          className="mt-3 text-[12px] font-bold uppercase tracking-[0.12em] text-[#758d9f]"
        >
          {artifact.pages} {artifact.pages === 1 ? "page" : "pages"} · PDF
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <a
            data-testid={`artifact-view-${artifact.slug}`}
            href={fileHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => fire("sample_artifact_viewed")}
            className="inline-flex items-center gap-1.5 text-[14px] font-extrabold text-[#4a6984] underline decoration-[#96ded1] decoration-2 underline-offset-4 transition-colors hover:text-[#1c2d3d]"
          >
            View PDF <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a
            data-testid={`artifact-download-${artifact.slug}`}
            href={fileHref}
            download
            onClick={() => fire("sample_artifact_downloaded", "pdf")}
            className="inline-flex items-center gap-1.5 text-[14px] font-extrabold text-[#4a6984] underline decoration-[#96ded1] decoration-2 underline-offset-4 transition-colors hover:text-[#1c2d3d]"
          >
            Download PDF <Download className="h-3.5 w-3.5" />
          </a>
          {pptxHref && (
            <a
              data-testid={`artifact-pptx-${artifact.slug}`}
              href={pptxHref}
              onClick={() => fire("sample_artifact_downloaded", "pptx")}
              className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#758d9f] transition-colors hover:text-[#4a6984]"
            >
              Download .pptx
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function LessonBlock({ lesson }) {
  const meta = [
    ["Grade", lesson.grade],
    ["Subject", lesson.subject],
    ["Strand", lesson.strand],
    ["Duration", `${lesson.duration_minutes} min`],
    ["Skill Focus", lesson.skill_focus],
  ];
  const pkg = lesson.artifacts.find((a) => a.slug === "complete-package");
  const pkgHref = pkg ? `${FILES_BASE(lesson.slug)}/${pkg.file}` : null;

  return (
    <section data-testid={`lesson-${lesson.slug}`} className="mt-10">
      {/* Lesson header */}
      <header className="mx-auto max-w-3xl px-5">
        <h2
          data-testid="lesson-title"
          className="text-2xl font-black leading-tight tracking-tight text-[#1c2d3d] sm:text-3xl"
        >
          {lesson.title}
        </h2>
        <p
          data-testid="lesson-subtitle"
          className="mt-2 text-[15px] leading-relaxed text-[#4a6984]"
        >
          {lesson.subtitle}
        </p>

        <div
          data-testid="lesson-meta"
          className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl border border-[#4a6984]/15 bg-white px-5 py-4 sm:grid-cols-3"
        >
          {meta.map(([label, value]) => (
            <MetaItem key={label} label={label} value={value} />
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="lesson-expectations">
          {lesson.expectation_codes.map((code) => {
            const entry =
              expectationText[`${lesson.grade}|${lesson.subject}|${code}`];
            return (
              <ExpectationPill
                key={code}
                code={code}
                text={entry ? entry.description : ""}
              />
            );
          })}
        </div>

        {pkgHref && (
          <a
            data-testid="lesson-download-all"
            href={pkgHref}
            download
            onClick={() =>
              track("sample_package_downloaded", {
                lesson_slug: lesson.slug,
                artifact_slug: pkg.slug,
                pages: pkg.pages,
                source: "download_everything_top",
              })
            }
            className="mt-5 inline-flex items-center gap-2 rounded-lg border-2 border-[#4a6984] bg-white px-6 py-3 text-[15px] font-extrabold text-[#4a6984] transition-colors hover:bg-[#4a6984] hover:text-white"
          >
            <Download className="h-4 w-4" /> Download everything ({pkg.pages} pages, one PDF)
          </a>
        )}
      </header>

      {/* Seven artifact cards, manifest (teaching) order */}
      <div className="mx-auto mt-8 flex max-w-3xl flex-col gap-4 px-5">
        {lesson.artifacts.map((artifact, i) => (
          <ArtifactCard
            key={artifact.slug}
            lesson={lesson}
            artifact={artifact}
            index={i}
            isPackage={artifact.slug === "complete-package"}
          />
        ))}
      </div>
    </section>
  );
}

export default function Samples() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Sample Lesson Package — Atomic Edu";
    return () => {
      document.title = prev;
    };
  }, []);

  const lessons = manifest.lessons || [];

  return (
    <main
      data-testid="samples-page"
      className="min-h-screen bg-[#f4f5f7] pb-24 font-[Montserrat]"
    >
      {/* 1. Page header — neutral, colleague voice */}
      <header className="mx-auto max-w-3xl px-5 pt-14 sm:pt-20">
        <p
          data-testid="samples-eyebrow"
          className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#758d9f]"
        >
          Sample Package
        </p>
        <h1
          data-testid="samples-headline"
          className="mt-3 text-3xl font-black leading-[1.08] tracking-tight text-[#1c2d3d] sm:text-4xl"
        >
          One lesson, every artifact it produces.
        </h1>
        <p
          data-testid="samples-subhead"
          className="mt-4 text-[15px] leading-relaxed text-[#4a6984]"
        >
          This is a complete lesson package — the plan, the deck, the student
          sheets, the assessment, the report-card language, all of it. Nothing
          removed. Open anything and read it the way you would before you teach it.
        </p>
      </header>

      {/* 2 + 3. Lesson header and the seven artifact cards */}
      {lessons.map((lesson) => (
        <LessonBlock key={lesson.slug} lesson={lesson} />
      ))}

      {/* 4. Privacy strip — unchanged copy */}
      <div
        data-testid="samples-privacy-strip"
        className="mt-14 border-y border-[#96ded1]/40 bg-[#96ded1]/[0.08]"
      >
        <div className="mx-auto max-w-3xl px-5 py-3 text-[13px] leading-snug text-[#4a6984] sm:text-sm">
          <span className="font-extrabold text-[#1c2d3d]">
            No student names appear anywhere on this page — or anywhere in Atomic Edu.
          </span>{" "}
          These lessons were built for classrooms, not for students on file.{" "}
          <Link
            to="/privacy"
            data-testid="samples-privacy-link"
            className="whitespace-nowrap font-bold text-[#4a6984] underline underline-offset-2 hover:text-[#1c2d3d]"
          >
            How that works →
          </Link>
        </div>
      </div>

      {/* 5. One closing CTA — the only CTA on the page */}
      <section
        data-testid="samples-cta"
        className="mx-auto mt-16 max-w-3xl px-5 text-center"
      >
        <Link
          to="/free-lesson-generator"
          data-testid="samples-cta-button"
          className="inline-flex items-center gap-2 rounded-lg bg-[#96ded1] px-8 py-4 text-base font-extrabold text-[#1c2d3d] transition-colors hover:bg-[#7bcbc0]"
        >
          Make one for your class <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </main>
  );
}
